import { useEffect } from 'react';
import {
  Alert,
  Card,
  Icon,
  Label,
  Link,
  Tag,
  Text,
} from '@nimbus-ds/components';
import { CheckIcon, ChevronUpIcon, StarIcon } from '@nimbus-ds/icons';
import { Trans } from 'react-i18next';
import { PaymentProviderDto } from '@tiendanube/common';
import {
  PaymentProvidersCode,
  muvemPagoOrPagoNubeIds,
} from '@tiendanube/common/src/domains/payments/providers/PaymentProvidersCode';
import { MethodNameAndImage, Stack } from 'commons/components';
import {
  usePatchProviderStatusForNuvemPagoBrasil,
  useUpsellBanner,
} from 'domains/Payments/PaymentProviders/hooks';
import useTranslationPayments from 'domains/Payments/useTranslationPayments';
import { PaymentMethodsTable, ProvidersActions } from '.';
import { PaymentMethodsEmptyTable } from './PaymentMethodsEmptyTable';
import DeprecatedPaymentProviderWarning from '../../DeprecatedPaymentProviderWarning';
import { NuvemPagoAdoptAlertTip } from '../../NuvemPagoAdoptAlertTip';
import { OnPaymentProviderActionType } from '../../PaymentProviderActionModal';
import { PaymentProviderStatusAlert } from '../../PaymentProviderStatusAlert';
import { PaymentProviderStatusLabel } from '../../PaymentProviderStatusLabel';

type PaymentProviderBodyProps = {
  paymentProvider: PaymentProviderDto;
  onAction: OnPaymentProviderActionType;
  onClose: () => void;
};

export function PaymentProviderBody({
  paymentProvider,
  onAction,
  onClose,
}: Readonly<PaymentProviderBodyProps>) {
  const t = useTranslationPayments();
  const { id, logo, features, rates, paymentMethodsConfiguration } =
    paymentProvider;
  const isNuvemPagoOrPagoNube = muvemPagoOrPagoNubeIds.includes(id);
  const hasPaymentMethods = rates.length > 0;

  const { isNuvemPagoBr, providerStatusLabelProps, trackProviderStatus } =
    usePatchProviderStatusForNuvemPagoBrasil(paymentProvider);

  const {
    isActive,
    mustCompleteInstallation,
    processingInstallation,
    isDeprecated,
  } = isNuvemPagoBr ? providerStatusLabelProps : paymentProvider;

  useEffect(() => {
    if (isNuvemPagoBr) {
      trackProviderStatus();
    }
  }, [isNuvemPagoBr, trackProviderStatus]);

  const { showUpsellBanner, upsellBanner } = useUpsellBanner(
    id,
    paymentMethodsConfiguration,
  );

  return (
    <>
      <Card.Header>
        <Stack justify="space-between">
          <MethodNameAndImage image={logo} isPagoNube={isNuvemPagoOrPagoNube} />
          <Stack>
            <Stack column align="flex-end" spacing="tight">
              <PaymentProviderStatusLabel
                isActive={isActive}
                mustCompleteInstallation={mustCompleteInstallation}
                processingInstallation={processingInstallation}
                isDeprecated={isDeprecated}
              />
              {isNuvemPagoOrPagoNube && (
                <Tag appearance="primary">
                  <StarIcon size={12} />
                  {t('paymentProvider.recommended')}
                </Tag>
              )}
            </Stack>
            {!isNuvemPagoOrPagoNube && (
              <Icon
                onClick={onClose}
                color="neutral-textHigh"
                source={<ChevronUpIcon />}
                cursor="pointer"
              />
            )}
          </Stack>
        </Stack>
      </Card.Header>
      <Card.Body>
        <Stack column align="flex-start">
          {isNuvemPagoOrPagoNube && (
            <PaymentProviderStatusAlert paymentProvider={paymentProvider} />
          )}
          {isNuvemPagoBr && <NuvemPagoAdoptAlertTip isActive={isActive} />}
          <Stack wrap spacing="tight">
            {features.map((feature) => (
              <Label key={feature}>
                <Icon color="primary-textHigh" source={<CheckIcon />} />
                <Text>{t(`paymentFeaturesCard.features.${feature}`)}</Text>
              </Label>
            ))}
          </Stack>
          {paymentProvider.id === PaymentProvidersCode.mercadopago &&
            paymentProvider.isDeprecated && (
              <Alert appearance="warning">
                <Text>
                  <Trans
                    t={t}
                    i18nKey="paymentProvider.settings.mercadopago.deprecatedAlert"
                    components={{
                      TyCLink: (
                        <Link
                          as="a"
                          appearance="neutral"
                          href={t(
                            'paymentProvider.settings.mercadopago.tycLink',
                          )}
                          target="_blank"
                        >
                          {' '}
                        </Link>
                      ),
                    }}
                  />
                </Text>
              </Alert>
            )}
          {paymentProvider.id === PaymentProvidersCode.paypal &&
            paymentProvider.isDeprecated && (
              <DeprecatedPaymentProviderWarning
                id={PaymentProvidersCode.paypal}
                name="PayPal"
              />
            )}
          {hasPaymentMethods ? (
            <PaymentMethodsTable
              providerId={id}
              rates={rates}
              paymentMethodsConfiguration={paymentMethodsConfiguration}
            />
          ) : (
            <PaymentMethodsEmptyTable provider={paymentProvider} />
          )}
          {showUpsellBanner && upsellBanner}
        </Stack>
        <ProvidersActions
          paymentProvider={paymentProvider}
          onAction={onAction}
        />
      </Card.Body>
    </>
  );
}
